import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	TextField,
	DialogActions,
	Button,
	MenuItem,
	FormControl,
	FormLabel,
	RadioGroup,
	Radio,
	FormControlLabel,
} from "@mui/material";
import { useCurrenciesQuery } from "../../services/queryHooks";
import { useTranslation } from "react-i18next";
import { Loading } from "../../components/common/Loading";
import { useUpdateUserSettingsMutation } from "../../services/queryHooks";
import { validateTextField } from "../../utils/validation";

export default function UserDataSettingsModal({
	isOpen,
	onClose,
	initialData,
}) {
	const { t, i18n } = useTranslation();
	const { data: currencies, isPending: currenciesLoading } =
		useCurrenciesQuery();
	const { mutate: updateUserSettings, isLoading: isUpdating } =
		useUpdateUserSettingsMutation();
	const [form, setForm] = useState({
		name: "",
		surname: "",
		currency: "",
		language: "",
	});
	const [errors, setErrors] = useState({
		name: "",
		surname: "",
	});

	useEffect(() => {
		if (isOpen && initialData) {
			setForm(initialData);
		}
	}, [isOpen, initialData]);

	const handleChange = (field) => (e) => {
		setForm((prev) => ({
			...prev,
			[field]: e.target.value,
		}));
	};

	const [budgetChangeOption, setBudgetChangeOption] = useState("doNothing");
	const [exchangeRate, setExchangeRate] = useState(1);

	const validateInputs = () => {
		let isValid = true;

		const newErrors = {
			name: "",
			surname: "",
		};

		const nameError = validateTextField(form.name, t, {
			fieldName: t("nameOfUser"),
		});
		if (nameError) {
			newErrors.name = nameError;
			isValid = false;
		}

		const surnameError = validateTextField(form.surname, t, {
			fieldName: t("surname"),
		});
		if (surnameError) {
			newErrors.surname = surnameError;
			isValid = false;
		}

		setErrors(newErrors);
		return isValid;
	};

	const submitForm = (event) => {
		event.preventDefault();

		if (!validateInputs()) return;

		const payload = {
			name: form.name,
			surname: form.surname,
			currency: form.currency,
			budgetOption: budgetChangeOption,
			budgetConversionRate: exchangeRate,
		};

		updateUserSettings(payload, {
			onSuccess: () => {
				if (form.language !== i18n.language) {
					i18n.changeLanguage(form.language);
				}
				onClose();
			},
			onError: (error) => {
				console.error("Error updating the user:", error);
			},
		});
	};

	if (currenciesLoading) return <Loading />;

	return (
		<Dialog
			open={isOpen}
			onClose={onClose}
			disableEnforceFocus
			maxWidth="xs"
			fullWidth
		>
			<DialogTitle>{t("accountSettings")}</DialogTitle>
			<form onSubmit={submitForm}>
				<DialogContent
					sx={{ display: "flex", flexDirection: "column", gap: 2 }}
				>
					<TextField
						label={t("nameOfUser")}
						value={form.name}
						onChange={handleChange("name")}
						error={!!errors.name}
						helperText={errors.name}
					/>
					<TextField
						label={t("surname")}
						value={form.surname}
						onChange={handleChange("surname")}
						error={!!errors.surname}
						helperText={errors.surname}
					/>

					<TextField
						select
						label={t("defaultCurrency")}
						fullWidth
						value={form.currency}
						onChange={handleChange("currency")}
					>
						{currencies.map((currency) => (
							<MenuItem key={currency} value={currency}>
								{currency}
							</MenuItem>
						))}
					</TextField>
					{form.currency !== initialData.currency && (
						<FormControl>
							<FormLabel id="demo-controlled-radio-buttons-group">
								{t("whatToDoWithBudgets")}
							</FormLabel>
							<RadioGroup
								aria-labelledby="demo-controlled-radio-buttons-group"
								name="controlled-radio-buttons-group"
								value={budgetChangeOption}
								onChange={(e) =>
									setBudgetChangeOption(e.target.value)
								}
							>
								<FormControlLabel
									value="doNothing"
									control={<Radio />}
									label={t("doNothing")}
								/>
								<FormControlLabel
									value="deleteAllBudgets"
									control={<Radio />}
									label={t("deleteAllBudgets")}
								/>
								<FormControlLabel
									value="exchangeBudgetsToNewCurrency"
									control={<Radio />}
									label={t("exchangeBudgetsToNewCurrency")}
								/>
							</RadioGroup>
						</FormControl>
					)}
					{budgetChangeOption === "exchangeBudgetsToNewCurrency" && (
						<TextField
							sx={{ marginBottom: 2 }}
							label={t("budgetConversionRate")}
							type="number"
							value={exchangeRate}
							onChange={(e) => {
								let val = e.target.value;
								if (val.includes(".")) {
									const [intPart, decPart] = val.split(".");
									val = intPart + "." + decPart.slice(0, 2);
								}
								setExchangeRate(val);
							}}
						/>
					)}

					<TextField
						select
						label={t("language")}
						fullWidth
						value={form.language}
						onChange={handleChange("language")}
					>
						{i18n.options.supportedLngs
							.filter((lng) => lng !== "cimode")
							.map((language) => (
								<MenuItem key={language} value={language}>
									{language}
								</MenuItem>
							))}
					</TextField>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose}>{t("cancel")}</Button>
					<Button
						type="submit"
						variant="contained"
						disabled={isUpdating}
					>
						{t("save")}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
}
